import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search, Plus, Play, Sparkles, CheckCircle2, XCircle, Eye,
  RefreshCw, UserPlus, Send, BarChart3, Instagram,
  ExternalLink, Trash2, ToggleLeft, ToggleRight, ChevronDown,
  CheckSquare, Square, TrendingUp, AlertTriangle, Zap, Hash, Users,
  Clock, RotateCcw,
} from 'lucide-react'
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import { igDiscoveryApi } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Input, { Select, Textarea } from '../../components/ui/Input'
import Badge from '../../components/ui/Badge'
import ScrollableTabs from '../../components/ui/ScrollableTabs'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import KpiCard from '../../components/ui/KpiCard'
import { useConfirm } from '../../components/ui/ConfirmDialog'
import { fmtDate, fmtRelative } from '../../utils/date'

// --- Constants ---

const STAGES = [
  { value: '', label: 'All Stages' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'discovered', label: 'Discovered' },
  { value: 'researching', label: 'Researching' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'outreach_sent', label: 'Outreach Sent' },
  { value: 'responded', label: 'Responded' },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
]

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'purple'

const STAGE_VARIANT: Record<string, BadgeVariant> = {
  pending_review: 'default',
  discovered: 'info',
  researching: 'info',
  qualified: 'purple',
  outreach_sent: 'warning',
  responded: 'success',
  converted: 'success',
  rejected: 'danger',
}

const REJECTION_REASONS = [
  { value: 'not_a_seller', label: 'Not a seller' },
  { value: 'too_small', label: 'Too small / not active' },
  { value: 'already_using_competitor', label: 'Already using a competitor' },
  { value: 'not_in_target_market', label: 'Not in target market' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'other', label: 'Other' },
]

const QUERY_CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'general', label: 'General' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'food', label: 'Food & Beverage' },
  { value: 'beauty', label: 'Beauty & Wellness' },
  { value: 'homedecor', label: 'Home & Decor' },
  { value: 'kids', label: 'Kids' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'city', label: 'City' },
  { value: 'regional', label: 'Regional' },
  { value: 'hashtag', label: 'Hashtag (Google)' },
  { value: 'ig_hashtag', label: 'Hashtag (Instagram)' },
  { value: 'pets', label: 'Pets' },
]

const OUTREACH_TYPES = [
  { value: 'dm', label: 'Instagram DM' },
  { value: 'email', label: 'Email' },
  { value: 'call', label: 'Phone Call' },
  { value: 'comment', label: 'Comment' },
  { value: 'other', label: 'Other' },
]

// --- Helper components ---

function ScoreBadge({ score }: { score: number | null }) {
  if (score == null) return <span className="text-xs text-gray-400">--</span>
  if (score >= 60) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">HOT {score}</span>
  if (score >= 30) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">WARM {score}</span>
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">COLD {score}</span>
}

function SourceBadge({ source }: { source: string | null }) {
  if (!source) return null
  const map: Record<string, { label: string; color: string }> = {
    google: { label: 'Google', color: 'bg-blue-50 text-blue-600' },
    discovery: { label: 'Google', color: 'bg-blue-50 text-blue-600' },
    hashtag: { label: 'Hashtag', color: 'bg-purple-50 text-purple-600' },
    hashtag_mention: { label: 'Hashtag', color: 'bg-purple-50 text-purple-600' },
    manual: { label: 'Manual', color: 'bg-gray-100 text-gray-600' },
  }
  const { label, color } = map[source] || { label: source, color: 'bg-gray-100 text-gray-600' }
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${color}`}>{label}</span>
}

function ProfileAvatar({ handle, size = 'md' }: { handle: string; size?: 'sm' | 'md' | 'lg' }) {
  const letter = (handle || '?').replace('@', '')[0]?.toUpperCase() || '?'
  const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-14 h-14 text-lg' }
  return (
    <div className={`${sizes[size]} rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold flex-shrink-0`}>
      {letter}
    </div>
  )
}

function StageBadge({ stage }: { stage: string }) {
  return <Badge variant={STAGE_VARIANT[stage] || 'default'}>{(stage || 'unknown').replace(/_/g, ' ')}</Badge>
}

function AssignmentSelector({ currentUserId, onAssign }: { currentUserId: number | null; onAssign: (userId: number | null) => void }) {
  const { data } = useQuery({
    queryKey: ['users'],
    queryFn: () => import('../../api/endpoints').then(m => m.usersApi.list()),
    staleTime: 5 * 60 * 1000,
  })
  const users = (data?.users || []).filter((u: any) => u.is_active && ['admin', 'manager', 'marketing', 'sales_rep'].includes(u.role))
  return (
    <Select
      label="Assigned To"
      options={[
        { value: '', label: 'Unassigned' },
        ...users.map((u: any) => ({ value: String(u.id), label: u.full_name || u.username })),
      ]}
      value={currentUserId ? String(currentUserId) : ''}
      onChange={(e) => onAssign(e.target.value ? Number(e.target.value) : null)}
      className="w-44"
    />
  )
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '--'
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(n)
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return '--'
  return (n * 100).toFixed(1) + '%'
}

// --- Main page ---

export default function IgDiscoveryPage() {
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager'
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('ig_discovery_tab')
    return saved && ['review', 'prospects', 'jobs', 'queries', 'stats'].includes(saved) ? saved : 'review'
  })
  const [confirmNode, confirm] = useConfirm()

  // Fetch counts for tab badges
  const { data: statsData } = useQuery({
    queryKey: ['ig-stats'],
    queryFn: () => igDiscoveryApi.getStats(),
    refetchInterval: 30_000,
  })
  const stats = statsData?.data || {}
  const byStage = stats.by_stage || {}
  const pendingCount = byStage.pending_review || 0
  const prospectsCount = (stats.total_prospects || 0) - pendingCount - (byStage.rejected || 0)
  const jobsCount = stats.total_jobs || 0
  const queriesCount = stats.active_queries || 0

  const tabs = [
    { key: 'review', label: 'Review', icon: <Eye size={16} />, count: pendingCount || undefined },
    { key: 'prospects', label: 'Prospects', icon: <Instagram size={16} />, count: prospectsCount || undefined },
    { key: 'jobs', label: 'Jobs', icon: <Play size={16} />, count: jobsCount || undefined },
    { key: 'queries', label: 'Queries', icon: <Search size={16} />, count: queriesCount || undefined },
    { key: 'stats', label: 'Stats', icon: <BarChart3 size={16} /> },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Instagram Seller Discovery</h1>
          <p className="text-sm text-gray-500 mt-0.5">Find and qualify Instagram sellers for Storepecker and UGCommerce</p>
        </div>
      </div>

      <ScrollableTabs tabs={tabs} activeKey={activeTab} onChange={(key) => { setActiveTab(key); localStorage.setItem('ig_discovery_tab', key) }} />

      {activeTab === 'review' && <ReviewTab confirm={confirm} />}
      {activeTab === 'prospects' && <ProspectsTab isAdmin={isAdmin} />}
      {activeTab === 'jobs' && <JobsTab />}
      {activeTab === 'queries' && <QueriesTab confirm={confirm} />}
      {activeTab === 'stats' && <StatsTab />}

      {confirmNode}
    </div>
  )
}

// =========================
// Tab 1: Review Queue
// =========================

function ReviewTab({ confirm }: { confirm: ReturnType<typeof useConfirm>[1] }) {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [rejectingId, setRejectingId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('not_a_seller')

  const { data, isLoading } = useQuery({
    queryKey: ['ig-review'],
    queryFn: () => igDiscoveryApi.listReview({ stage: 'pending_review', limit: '50' }),
  })

  const approveMut = useMutation({
    mutationFn: (id: number) => igDiscoveryApi.reviewProspect(id, { action: 'approve' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-review'] }),
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      igDiscoveryApi.reviewProspect(id, { action: 'reject', rejection_reason: reason }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-review'] }); setRejectingId(null) },
  })

  const bulkMut = useMutation({
    mutationFn: (data: { prospect_ids: number[]; action: string; rejection_reason?: string }) =>
      igDiscoveryApi.bulkReview(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-review'] }); setSelected(new Set()) },
  })

  const prospects: any[] = data?.data ?? []
  const allSelected = prospects.length > 0 && selected.size === prospects.length

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(prospects.map((p: any) => p.id)))
  }

  const handleBulkApprove = async () => {
    if (selected.size === 0) return
    const ok = await confirm({ message: `Approve ${selected.size} prospect(s)?`, title: 'Bulk Approve', confirmLabel: 'Approve', confirmVariant: 'primary' })
    if (ok) bulkMut.mutate({ prospect_ids: [...selected], action: 'approve' })
  }

  const handleBulkReject = async () => {
    if (selected.size === 0) return
    const ok = await confirm({ message: `Reject ${selected.size} prospect(s)?`, title: 'Bulk Reject', confirmLabel: 'Reject' })
    if (ok) bulkMut.mutate({ prospect_ids: [...selected], action: 'reject', rejection_reason: 'not_a_seller' })
  }

  if (isLoading) return <LoadingState message="Loading review queue..." />

  if (prospects.length === 0) {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="Review queue is empty"
        description="All caught up! Run a new discovery job to find more sellers."
      />
    )
  }

  return (
    <div className="space-y-3">
      {/* Bulk actions bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={toggleAll} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
        {selected.size > 0 && (
          <>
            <span className="text-sm text-gray-500">{selected.size} selected</span>
            <Button size="sm" onClick={handleBulkApprove} loading={bulkMut.isPending}>Approve</Button>
            <Button size="sm" variant="danger" onClick={handleBulkReject} loading={bulkMut.isPending}>Reject</Button>
          </>
        )}
      </div>

      {/* Card grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {prospects.map((p: any) => (
          <div key={p.id} className="border border-gray-200 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow">
            <div className="flex items-start gap-3">
              <button onClick={() => toggleSelect(p.id)} className="mt-0.5 text-gray-400 hover:text-gray-700">
                {selected.has(p.id) ? <CheckSquare size={18} className="text-indigo-600" /> : <Square size={18} />}
              </button>
              <ProfileAvatar handle={p.ig_handle} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <a href={`https://instagram.com/${p.ig_handle}`} target="_blank" rel="noopener noreferrer"
                    className="font-medium text-gray-900 text-sm truncate hover:text-indigo-600 transition-colors"
                    onClick={(e) => e.stopPropagation()}>
                    @{p.ig_handle}
                  </a>
                  <a href={`https://instagram.com/${p.ig_handle}`} target="_blank" rel="noopener noreferrer"
                    className="text-gray-400 hover:text-indigo-600 flex-shrink-0" title="Open in Instagram"
                    onClick={(e) => e.stopPropagation()}>
                    <ExternalLink size={13} />
                  </a>
                  <ScoreBadge score={p.auto_score} />
                  <SourceBadge source={p.discovery_source} />
                </div>
                {p.display_name && <p className="text-xs text-gray-500 truncate">{p.display_name}</p>}
              </div>
            </div>

            {p.bio && (
              <p className="text-xs text-gray-600 mt-2 line-clamp-2">{p.bio}</p>
            )}

            <div className="flex gap-4 mt-3 text-xs text-gray-500">
              <span>{fmtNum(p.follower_count)} followers</span>
              <span>{fmtPct(p.engagement_rate)} eng.</span>
              {p.post_count != null && <span>{fmtNum(p.post_count)} posts</span>}
            </div>

            {/* Reject reason dropdown */}
            {rejectingId === p.id ? (
              <div className="mt-3 space-y-2">
                <Select
                  label="Reason"
                  options={REJECTION_REASONS}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button size="sm" variant="danger" loading={rejectMut.isPending}
                    onClick={() => rejectMut.mutate({ id: p.id, reason: rejectReason })}>
                    Confirm Reject
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRejectingId(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => approveMut.mutate(p.id)} loading={approveMut.isPending && approveMut.variables === p.id}>
                  <CheckCircle2 size={14} /> Approve
                </Button>
                <Button size="sm" variant="danger" onClick={() => setRejectingId(p.id)}>
                  <XCircle size={14} /> Reject
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// =========================
// Tab 2: Prospects
// =========================

function ProspectsTab({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState({ stage: '', search: '' })
  const [offset, setOffset] = useState(0)
  const limit = 25
  const [addOpen, setAddOpen] = useState(false)
  const [addHandle, setAddHandle] = useState('')
  const [addNotes, setAddNotes] = useState('')
  const [detailId, setDetailId] = useState<number | null>(null)

  const params = useMemo(() => {
    const p: Record<string, string> = { limit: String(limit), offset: String(offset) }
    if (filters.stage) p.stage = filters.stage
    if (filters.search) p.search = filters.search
    return p
  }, [filters, offset])

  const { data, isLoading } = useQuery({
    queryKey: ['ig-prospects', params],
    queryFn: () => igDiscoveryApi.listProspects(params),
  })

  const createMut = useMutation({
    mutationFn: (data: { ig_handle: string; notes?: string }) => igDiscoveryApi.createProspect(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-prospects'] }); setAddOpen(false); setAddHandle(''); setAddNotes('') },
  })

  const prospects: any[] = data?.data ?? []
  const total = (data as any)?.total ?? 0

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px] max-w-xs">
          <Input placeholder="Search by handle..." value={filters.search}
            onChange={(e) => { setFilters((f) => ({ ...f, search: e.target.value })); setOffset(0) }} />
        </div>
        <div className="w-44">
          <Select options={STAGES} value={filters.stage}
            onChange={(e) => { setFilters((f) => ({ ...f, stage: e.target.value })); setOffset(0) }} />
        </div>
        <Button onClick={() => setAddOpen(true)}><Plus size={16} /> Add Prospect</Button>
      </div>

      {isLoading ? (
        <LoadingState message="Loading prospects..." />
      ) : prospects.length === 0 ? (
        <EmptyState icon={Instagram} title="No prospects found" description="Adjust your filters or run a discovery job to find new sellers." />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="pb-2 pr-3">Handle</th>
                  <th className="pb-2 pr-3">Name</th>
                  <th className="pb-2 pr-3 text-right">Followers</th>
                  <th className="pb-2 pr-3 text-right">Engagement</th>
                  <th className="pb-2 pr-3 text-center">Score</th>
                  <th className="pb-2 pr-3">Stage</th>
                  <th className="pb-2 pr-3">Tags</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {prospects.map((p: any) => (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setDetailId(p.id)}>
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        <ProfileAvatar handle={p.ig_handle} size="sm" />
                        <a href={`https://instagram.com/${p.ig_handle}`} target="_blank" rel="noopener noreferrer"
                          className="font-medium text-gray-900 hover:text-indigo-600 transition-colors"
                          onClick={(e) => e.stopPropagation()}>
                          @{p.ig_handle}
                        </a>
                        <a href={`https://instagram.com/${p.ig_handle}`} target="_blank" rel="noopener noreferrer"
                          className="text-gray-400 hover:text-indigo-600" title="Open in Instagram"
                          onClick={(e) => e.stopPropagation()}>
                          <ExternalLink size={12} />
                        </a>
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-gray-600 truncate max-w-[150px]">{p.display_name || '--'}</td>
                    <td className="py-2.5 pr-3 text-right text-gray-700">{fmtNum(p.follower_count)}</td>
                    <td className="py-2.5 pr-3 text-right text-gray-700">{fmtPct(p.engagement_rate)}</td>
                    <td className="py-2.5 pr-3 text-center"><ScoreBadge score={p.auto_score} /></td>
                    <td className="py-2.5 pr-3"><StageBadge stage={p.stage} /></td>
                    <td className="py-2.5 pr-3">
                      <div className="flex gap-1 flex-wrap">
                        {(p.tags || []).slice(0, 3).map((t: any) => (
                          <span key={t.id || t.name} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full">{t.name}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        <a href={`https://instagram.com/${p.ig_handle}`} target="_blank" rel="noopener noreferrer"
                          className="text-pink-500 hover:text-pink-700 text-xs font-medium flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}>
                          <Instagram size={13} /> IG
                        </a>
                        <button onClick={(e) => { e.stopPropagation(); setDetailId(p.id) }} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {prospects.map((p: any) => (
              <div key={p.id} className="border border-gray-200 rounded-xl p-3 bg-white" onClick={() => setDetailId(p.id)}>
                <div className="flex items-center gap-3">
                  <ProfileAvatar handle={p.ig_handle} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900 truncate">@{p.ig_handle}</span>
                      <a href={`https://instagram.com/${p.ig_handle}`} target="_blank" rel="noopener noreferrer"
                        className="text-gray-400 hover:text-indigo-600 flex-shrink-0" title="Open in Instagram"
                        onClick={(e) => e.stopPropagation()}>
                        <ExternalLink size={12} />
                      </a>
                      <ScoreBadge score={p.auto_score} />
                    </div>
                    <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                      <span>{fmtNum(p.follower_count)} followers</span>
                      <span>{fmtPct(p.engagement_rate)} eng.</span>
                    </div>
                  </div>
                  <StageBadge stage={p.stage} />
                </div>
              </div>
            ))}
          </div>

          <Pagination total={total} limit={limit} offset={offset} onChange={setOffset} />
        </>
      )}

      {/* Add Prospect Modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Prospect Manually">
        <div className="space-y-4">
          <Input label="Instagram Handle" placeholder="e.g. thefashionstore" value={addHandle}
            onChange={(e) => setAddHandle(e.target.value.replace(/^@/, ''))} />
          <Textarea label="Notes (optional)" placeholder="Why are you adding this seller?" value={addNotes}
            onChange={(e) => setAddNotes(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={() => createMut.mutate({ ig_handle: addHandle, notes: addNotes || undefined })}
              loading={createMut.isPending} disabled={!addHandle.trim()}>
              Add Prospect
            </Button>
          </div>
        </div>
      </Modal>

      {/* Detail Modal */}
      {detailId && (
        <ProspectDetailModal id={detailId} isAdmin={isAdmin} onClose={() => setDetailId(null)} />
      )}
    </div>
  )
}

// =========================
// Prospect Detail Modal
// =========================

function ProspectDetailModal({ id, isAdmin, onClose }: { id: number; isAdmin: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [outreachOpen, setOutreachOpen] = useState(false)
  const [outreachForm, setOutreachForm] = useState({ touchpoint_type: 'dm', channel: 'instagram', subject: '', body: '' })
  const [convertOpen, setConvertOpen] = useState(false)
  const [convertForm, setConvertForm] = useState({ name: '', company: '', lead_source: 'instagram_discovery' })
  const [newTag, setNewTag] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['ig-prospect', id],
    queryFn: () => igDiscoveryApi.getProspect(id),
  })

  const { data: outreachData } = useQuery({
    queryKey: ['ig-outreach', id],
    queryFn: () => igDiscoveryApi.listOutreach(id),
  })

  const enrichMut = useMutation({
    mutationFn: () => igDiscoveryApi.enrichProspect(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-prospect', id] }),
  })

  const updateMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => igDiscoveryApi.updateProspect(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-prospect', id] }); queryClient.invalidateQueries({ queryKey: ['ig-prospects'] }) },
  })

  const outreachMut = useMutation({
    mutationFn: (data: { touchpoint_type: string; subject?: string; body?: string; channel?: string }) =>
      igDiscoveryApi.logOutreach(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-outreach', id] }); setOutreachOpen(false); setOutreachForm({ touchpoint_type: 'dm', channel: 'instagram', subject: '', body: '' }) },
  })

  const convertMut = useMutation({
    mutationFn: (data: { name: string; company?: string; lead_source?: string }) =>
      igDiscoveryApi.convertToLead(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-prospect', id] }); queryClient.invalidateQueries({ queryKey: ['ig-prospects'] }); setConvertOpen(false) },
  })

  const addTagMut = useMutation({
    mutationFn: (tags: string[]) => igDiscoveryApi.addTags(id, tags),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-prospect', id] }); setNewTag('') },
  })

  const removeTagMut = useMutation({
    mutationFn: (tagId: number) => igDiscoveryApi.removeTag(id, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-prospect', id] }),
  })

  const p: any = data?.data
  const outreachList: any[] = outreachData?.data ?? []

  if (isLoading || !p) return <Modal open onClose={onClose} title="Prospect Detail" size="lg"><LoadingState /></Modal>

  return (
    <Modal open onClose={onClose} title={`@${p.ig_handle}`} size="lg">
      <div className="space-y-6">
        {/* Profile header */}
        <div className="flex items-start gap-4">
          <ProfileAvatar handle={p.ig_handle} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">@{p.ig_handle}</h3>
              <StageBadge stage={p.stage} />
              <ScoreBadge score={p.auto_score} />
              <SourceBadge source={p.discovery_source} />
            </div>
            {p.display_name && <p className="text-sm text-gray-600 mt-0.5">{p.display_name}</p>}
            {p.bio && <p className="text-sm text-gray-500 mt-1">{p.bio}</p>}
            {p.ig_handle && (
              <a href={`https://instagram.com/${p.ig_handle}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mt-1">
                View on Instagram <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {[
            { label: 'Followers', value: fmtNum(p.follower_count) },
            { label: 'Following', value: fmtNum(p.following_count) },
            { label: 'Posts', value: fmtNum(p.post_count) },
            { label: 'Engagement', value: fmtPct(p.engagement_rate) },
            { label: 'Avg Likes', value: fmtNum(p.avg_likes) },
            { label: 'Avg Comments', value: fmtNum(p.avg_comments) },
          ].map((m) => (
            <div key={m.label} className="bg-gray-50 rounded-lg p-2.5 text-center">
              <p className="text-xs text-gray-500">{m.label}</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Stage + Assignment + Actions */}
        <div className="space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <Select label="Stage" options={STAGES.filter((s) => s.value !== '')} value={p.stage}
              onChange={(e) => updateMut.mutate({ stage: e.target.value })} className="w-40" />
            <AssignmentSelector
              currentUserId={p.assigned_to}
              onAssign={(userId) => updateMut.mutate({ assigned_to: userId || null })}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="secondary" onClick={() => enrichMut.mutate()} loading={enrichMut.isPending}>
              <RefreshCw size={14} /> Enrich
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setOutreachOpen(true)}>
              <Send size={14} /> Log Outreach
            </Button>
            {isAdmin && p.stage !== 'converted' && (
              <Button size="sm" onClick={() => { setConvertForm({ name: p.display_name || p.ig_handle, company: '', lead_source: 'instagram_discovery' }); setConvertOpen(true) }}>
                <UserPlus size={14} /> Convert to Lead
              </Button>
            )}
          </div>
        </div>

        {/* Tags */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Tags</h4>
          <div className="flex flex-wrap gap-1.5 items-center">
            {(p.tags || []).map((t: any) => (
              <span key={t.id} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                {t.name}
                <button onClick={() => removeTagMut.mutate(t.id)} className="text-gray-400 hover:text-red-500">
                  <XCircle size={12} />
                </button>
              </span>
            ))}
            <form onSubmit={(e) => { e.preventDefault(); if (newTag.trim()) addTagMut.mutate([newTag.trim()]) }}
              className="inline-flex items-center gap-1">
              <input type="text" placeholder="Add tag..." value={newTag} onChange={(e) => setNewTag(e.target.value)}
                className="text-xs border border-gray-200 rounded-full px-2 py-0.5 w-24 outline-none focus:border-indigo-400" />
            </form>
          </div>
        </div>

        {/* Outreach timeline */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Outreach History</h4>
          {outreachList.length === 0 ? (
            <p className="text-xs text-gray-400">No outreach logged yet.</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {outreachList.map((o: any) => (
                <div key={o.id} className="border border-gray-100 rounded-lg p-2.5 text-sm">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Badge variant="info">{o.touchpoint_type}</Badge>
                    {o.channel && <span>{o.channel}</span>}
                    <span>{fmtRelative(o.created_at)}</span>
                  </div>
                  {o.subject && <p className="font-medium text-gray-800 mt-1">{o.subject}</p>}
                  {o.body && <p className="text-gray-600 text-xs mt-0.5 line-clamp-2">{o.body}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Score breakdown */}
        {p.score_breakdown && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Score Breakdown</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(p.score_breakdown).map(([key, val]) => (
                <div key={key} className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-gray-500 uppercase">{key.replace(/_/g, ' ')}</p>
                  <p className="text-sm font-semibold text-gray-900">{String(val)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Log Outreach sub-modal */}
      <Modal open={outreachOpen} onClose={() => setOutreachOpen(false)} title="Log Outreach" size="sm">
        <div className="space-y-3">
          <Select label="Type" options={OUTREACH_TYPES} value={outreachForm.touchpoint_type}
            onChange={(e) => setOutreachForm((f) => ({ ...f, touchpoint_type: e.target.value }))} />
          <Input label="Channel" placeholder="e.g. instagram, email" value={outreachForm.channel}
            onChange={(e) => setOutreachForm((f) => ({ ...f, channel: e.target.value }))} />
          <Input label="Subject (optional)" value={outreachForm.subject}
            onChange={(e) => setOutreachForm((f) => ({ ...f, subject: e.target.value }))} />
          <Textarea label="Message" placeholder="What did you say?" value={outreachForm.body}
            onChange={(e) => setOutreachForm((f) => ({ ...f, body: e.target.value }))} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOutreachOpen(false)}>Cancel</Button>
            <Button onClick={() => outreachMut.mutate(outreachForm)} loading={outreachMut.isPending}
              disabled={!outreachForm.body.trim()}>
              Save Outreach
            </Button>
          </div>
        </div>
      </Modal>

      {/* Convert to Lead sub-modal */}
      <Modal open={convertOpen} onClose={() => setConvertOpen(false)} title="Convert to CRM Lead" size="sm">
        <div className="space-y-3">
          <Input label="Lead Name" value={convertForm.name}
            onChange={(e) => setConvertForm((f) => ({ ...f, name: e.target.value }))} />
          <Input label="Company (optional)" value={convertForm.company}
            onChange={(e) => setConvertForm((f) => ({ ...f, company: e.target.value }))} />
          <Input label="Lead Source" value={convertForm.lead_source}
            onChange={(e) => setConvertForm((f) => ({ ...f, lead_source: e.target.value }))} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConvertOpen(false)}>Cancel</Button>
            <Button onClick={() => convertMut.mutate(convertForm)} loading={convertMut.isPending}
              disabled={!convertForm.name.trim()}>
              Convert to Lead
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}

// =========================
// Tab 3: Discovery Jobs
// =========================

function JobsTab() {
  const queryClient = useQueryClient()
  const [expandedHistoryId, setExpandedHistoryId] = useState<number | null>(null)
  const [showQueryDetail, setShowQueryDetail] = useState(false)
  const [lastResult, setLastResult] = useState<any>(null)
  const [querySelectOpen, setQuerySelectOpen] = useState(false)
  const [selectedQueryIds, setSelectedQueryIds] = useState<Set<number>>(new Set())

  // Fetch all jobs (most recent first)
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['ig-jobs'],
    queryFn: () => igDiscoveryApi.listJobs({ limit: '50' }),
  })

  // Fetch queries for category-based launch
  const { data: queriesData } = useQuery({
    queryKey: ['ig-queries'],
    queryFn: () => igDiscoveryApi.listQueries(),
  })

  const allJobs: any[] = jobsData?.data ?? []
  const allQueries: any[] = queriesData?.data ?? []

  // Determine the active job: most recent job that is pending, running, or paused,
  // OR the most recent completed/failed job if no work remains
  const activeJob = allJobs.length > 0 ? allJobs[0] : null

  // Detect stale "running" — user navigated away mid-request, HTTP aborted
  const isJobStaleRunning = (() => {
    if (!activeJob || activeJob.status !== 'running') return false
    const started = activeJob.started_at ? new Date(activeJob.started_at).getTime() : 0
    return Date.now() - started > 120_000
  })()

  const activeJobIsPaused = activeJob && (
    activeJob.status === 'paused' ||
    activeJob.status === 'pending' ||
    isJobStaleRunning ||
    (activeJob.status === 'completed' && (activeJob.queries_run ?? 0) < (activeJob.search_count ?? 0) && activeJob.search_count > 0)
  )

  // Fetch detailed progress for the active job (includes per-query status)
  const { data: progressData } = useQuery({
    queryKey: ['ig-job-progress', activeJob?.id],
    queryFn: () => igDiscoveryApi.getJobProgress(activeJob.id),
    enabled: !!activeJob,
    refetchInterval: activeJob?.status === 'running' ? 3000 : false,
  })
  const activeJobDetail = progressData?.data ?? null

  // Past jobs: everything except the active one
  const pastJobs = activeJob ? allJobs.slice(1) : []

  // Compute progress from the active job's query_results
  const queryResults: any[] = (activeJobDetail?.searches ?? activeJobDetail?.query_results ?? activeJob?.query_results ?? [])
  const totalQueries = activeJob?.search_count ?? queryResults.length
  const completedQueries = queryResults.filter((q: any) => q.status === 'completed').length
  const failedQueries = queryResults.filter((q: any) => q.status === 'failed').length
  const pendingQueries = queryResults.filter((q: any) => q.status === 'pending' || q.status === 'skipped').length
  // rate_limited queries are retried automatically on next batch
  const progressPct = totalQueries > 0 ? Math.round((completedQueries / totalQueries) * 100) : 0
  const hasSkippedQueries = activeJobIsPaused

  // Launch NEW job
  const launchMut = useMutation({
    mutationFn: (data?: { query_ids?: number[]; category?: string }) =>
      igDiscoveryApi.launchJob(data),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['ig-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['ig-stats'] })
      queryClient.invalidateQueries({ queryKey: ['ig-review'] })
      queryClient.invalidateQueries({ queryKey: ['ig-job-progress'] })
      setLastResult(resp?.data || null)
    },
  })

  // Continue EXISTING job
  const continueMut = useMutation({
    mutationFn: (jobId: number) => igDiscoveryApi.continueJob(jobId),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['ig-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['ig-stats'] })
      queryClient.invalidateQueries({ queryKey: ['ig-review'] })
      queryClient.invalidateQueries({ queryKey: ['ig-job-progress'] })
      setLastResult(resp?.data || null)
    },
  })

  // Abandon job
  const abandonMut = useMutation({
    mutationFn: (jobId: number) => igDiscoveryApi.abandonJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ig-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['ig-job-progress'] })
    },
  })

  const isMutationRunning = launchMut.isPending || continueMut.isPending
  const isJobRunning = activeJob?.status === 'running' && !isJobStaleRunning
  const isRunning = isMutationRunning || isJobRunning

  // Determine smart button state
  const getSmartButton = () => {
    if (isRunning) {
      return { label: 'Running...', variant: 'secondary' as const, disabled: true, icon: null }
    }
    if (!activeJob || activeJob.status === 'abandoned') {
      return { label: 'Start Discovery', variant: 'primary' as const, disabled: false, icon: <Play size={16} /> }
    }
    if (activeJobIsPaused) {
      return {
        label: `Continue (${completedQueries}/${totalQueries})`,
        variant: 'primary' as const,
        disabled: false,
        icon: <Play size={16} />,
      }
    }
    if (activeJob.status === 'failed') {
      return { label: 'Retry Failed', variant: 'primary' as const, disabled: false, icon: <RotateCcw size={16} /> }
    }
    // completed (fully) or no active job
    return { label: 'Start Discovery', variant: 'primary' as const, disabled: false, icon: <Play size={16} /> }
  }

  const smartBtn = getSmartButton()

  const handleSmartAction = () => {
    setLastResult(null)
    if (activeJob && activeJobIsPaused) {
      continueMut.mutate(activeJob.id)
    } else if (activeJob && activeJob.status === 'failed') {
      continueMut.mutate(activeJob.id)
    } else {
      // NEW sweep — open query selection modal
      // Pre-select all active queries
      setSelectedQueryIds(new Set(allQueries.filter((q: any) => q.is_active).map((q: any) => q.id)))
      setQuerySelectOpen(true)
    }
  }

  const handleLaunchWithSelection = () => {
    setQuerySelectOpen(false)
    setLastResult(null)
    const ids = Array.from(selectedQueryIds)
    launchMut.mutate(ids.length > 0 ? { query_ids: ids } : {})
  }

  const handleAbandon = () => {
    if (activeJob && !['completed', 'failed', 'abandoned'].includes(activeJob.status)) {
      abandonMut.mutate(activeJob.id)
    }
  }

  return (
    <div className="space-y-6">
      {/* ========== Active Campaign Section ========== */}
      <div className="border border-gray-200 rounded-xl bg-white p-5">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Discovery Campaign</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {!activeJob || ['completed', 'abandoned'].includes(activeJob.status)
                ? 'Select queries and run a discovery sweep to search Google for Instagram sellers.'
                : activeJobIsPaused
                  ? 'Click Continue to process the next batch. Each batch takes about 15 seconds.'
                  : activeJob.status === 'running'
                    ? 'Searching Google for Instagram seller profiles...'
                    : activeJob.status === 'failed'
                      ? 'The last run encountered errors. You can retry the failed queries.'
                      : 'Ready to start a new discovery run.'
              }
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant={smartBtn.variant}
              onClick={handleSmartAction}
              loading={isRunning}
              disabled={smartBtn.disabled}
            >
              {smartBtn.icon} {smartBtn.label}
            </Button>
            {activeJob && activeJobIsPaused && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAbandon}
                loading={abandonMut.isPending}
                title="Abandon this run and start fresh"
              >
                <XCircle size={14} /> Abandon
              </Button>
            )}
          </div>
        </div>

        {/* Query selection modal */}
        <Modal open={querySelectOpen} onClose={() => setQuerySelectOpen(false)} title="Select Queries for Discovery Run" size="lg">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{selectedQueryIds.size} of {allQueries.filter((q: any) => q.is_active).length} queries selected</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedQueryIds(new Set(allQueries.filter((q: any) => q.is_active).map((q: any) => q.id)))}
                  className="text-xs text-indigo-600 hover:underline"
                >Select All</button>
                <button
                  onClick={() => setSelectedQueryIds(new Set())}
                  className="text-xs text-gray-500 hover:underline"
                >Deselect All</button>
              </div>
            </div>

            {/* Category quick filters */}
            <div className="flex flex-wrap gap-1.5">
              {QUERY_CATEGORIES.filter(c => c.value).map((cat) => {
                const catIds = allQueries.filter((q: any) => q.is_active && q.category === cat.value).map((q: any) => q.id)
                const allSelected = catIds.length > 0 && catIds.every((id: number) => selectedQueryIds.has(id))
                return (
                  <button
                    key={cat.value}
                    onClick={() => {
                      const next = new Set(selectedQueryIds)
                      if (allSelected) {
                        catIds.forEach((id: number) => next.delete(id))
                      } else {
                        catIds.forEach((id: number) => next.add(id))
                      }
                      setSelectedQueryIds(next)
                    }}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      allSelected
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700 font-medium'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {cat.label} ({catIds.length})
                  </button>
                )
              })}
            </div>

            {/* Query list */}
            <div className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {allQueries.filter((q: any) => q.is_active).map((q: any) => (
                <label
                  key={q.id}
                  className="flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedQueryIds.has(q.id)}
                    onChange={() => {
                      const next = new Set(selectedQueryIds)
                      if (next.has(q.id)) next.delete(q.id)
                      else next.add(q.id)
                      setSelectedQueryIds(next)
                    }}
                    className="rounded mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{q.label}</p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{q.query_text}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{q.category}</span>
                    {q.results_count > 0 && (
                      <span className="text-[10px] text-gray-400">{q.results_count} found</span>
                    )}
                  </div>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setQuerySelectOpen(false)}>Cancel</Button>
              <Button onClick={handleLaunchWithSelection} disabled={selectedQueryIds.size === 0} loading={launchMut.isPending}>
                <Play size={14} /> Start Discovery ({selectedQueryIds.size} queries)
              </Button>
            </div>
          </div>
        </Modal>

        {/* Progress bar — only when there's an active job with data */}
        {activeJob && totalQueries > 0 && (
          <>
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-gray-700 font-medium">
                  {completedQueries}/{totalQueries} queries ({progressPct}%)
                </span>
                {hasSkippedQueries && (
                  <span className="text-xs text-amber-600">Paused — waiting to continue</span>
                )}
              </div>
              <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    activeJob.status === 'failed' ? 'bg-red-500' :
                    progressPct === 100 ? 'bg-green-500' :
                    'bg-indigo-500'
                  }`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-4 text-sm mb-3">
              <span className="text-gray-700">
                <strong className="text-green-700">{activeJob.new_prospects_found ?? 0}</strong> new prospects
              </span>
              <span className="text-gray-700">
                <strong className="text-blue-700">{activeJob.existing_prospects_updated ?? 0}</strong> updated
              </span>
              {failedQueries > 0 && (
                <span className="text-gray-700">
                  <strong className="text-red-700">{failedQueries}</strong> failed
                </span>
              )}
              {pendingQueries > 0 && (
                <span className="text-gray-500">
                  ~{Math.ceil(pendingQueries / 5)} batches remaining
                </span>
              )}
            </div>

            {/* Per-query status (collapsible) */}
            {queryResults.length > 0 && (
              <div>
                <button
                  onClick={() => setShowQueryDetail(!showQueryDetail)}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 mb-2"
                >
                  <ChevronDown size={14} className={`transition-transform ${showQueryDetail ? 'rotate-180' : ''}`} />
                  {showQueryDetail ? 'Hide' : 'Show'} query details
                </button>
                {showQueryDetail && (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {queryResults.map((qr: any, i: number) => {
                      const status = qr.status || 'pending'
                      const statusIcon = status === 'completed'
                        ? <CheckCircle2 size={13} className="text-green-500 flex-shrink-0" />
                        : status === 'failed'
                          ? <XCircle size={13} className="text-red-500 flex-shrink-0" />
                          : status === 'rate_limited'
                            ? <AlertTriangle size={13} className="text-amber-500 flex-shrink-0" />
                            : <Clock size={13} className="text-gray-400 flex-shrink-0" />
                      return (
                        <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2.5 py-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            {statusIcon}
                            <span className="text-gray-700 truncate">{qr.label || qr.query_text || `Query ${i + 1}`}</span>
                          </div>
                          <span className="text-gray-500 flex-shrink-0 ml-2">
                            {status === 'completed' ? `${qr.count ?? qr.results_count ?? 0} found` : status}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Running banner */}
        {launchMut.isPending && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            Running discovery — searching Google for Instagram seller profiles. This may take a minute...
          </div>
        )}

        {/* Result alerts — errors */}
        {lastResult && !launchMut.isPending && lastResult.errors?.length > 0 && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            <p className="font-semibold mb-1">API Issues Detected</p>
            {lastResult.errors.map((err: string, i: number) => (
              <p key={i} className="flex items-start gap-2">
                <XCircle size={14} className="mt-0.5 flex-shrink-0" />
                {err}
              </p>
            ))}
            {lastResult.queries_failed > 0 && (
              <p className="text-xs text-red-600 mt-2">
                {lastResult.queries_failed} of {(lastResult.queries_run ?? 0) + (lastResult.queries_failed ?? 0)} queries failed.
                Check your RapidAPI subscription and API key.
              </p>
            )}
          </div>
        )}

        {/* Result alerts — warnings */}
        {lastResult && !launchMut.isPending && lastResult.warnings?.length > 0 && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            {lastResult.warnings.map((w: string, i: number) => (
              <p key={i}>{w}</p>
            ))}
          </div>
        )}

        {/* Result alerts — success */}
        {lastResult && !launchMut.isPending && (
          <div className={`mt-3 p-3 rounded-lg text-sm border ${
            lastResult.errors?.length ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-green-50 border-green-200 text-green-800'
          }`}>
            <div className="flex items-center justify-between">
              <span>
                Discovery {lastResult.errors?.length ? 'partially completed' : 'complete'}:
                {' '}<strong>{lastResult.new_found ?? 0}</strong> new prospects,
                {' '}<strong>{lastResult.updated ?? 0}</strong> existing updated
                {lastResult.hashtag_found > 0 && <> ({lastResult.hashtag_found} from hashtags)</>}
                {' '}({lastResult.queries_run ?? '?'} queries run).
              </span>
              <button onClick={() => setLastResult(null)} className="text-xs font-medium ml-3 opacity-70 hover:opacity-100">Dismiss</button>
            </div>
            {(lastResult.queries_skipped ?? 0) > 0 && (
              <p className="text-xs mt-1 opacity-70">{lastResult.queries_skipped} queries skipped. Click Continue to process more.</p>
            )}
          </div>
        )}

        {/* Mutation error */}
        {launchMut.isError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            <p className="font-semibold">Discovery failed</p>
            <p>{(launchMut.error as Error)?.message || 'Unknown error — check server logs'}</p>
          </div>
        )}
      </div>

      {/* ========== Job History Section ========== */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Job History</h3>
        {jobsLoading ? (
          <LoadingState message="Loading jobs..." />
        ) : pastJobs.length === 0 ? (
          <p className="text-sm text-gray-400">No past jobs yet.</p>
        ) : (
          <div className="space-y-2">
            {pastJobs.map((j: any) => {
              const statusVariant: BadgeVariant = j.status === 'completed' ? 'success' : j.status === 'running' ? 'warning' : j.status === 'failed' ? 'danger' : 'default'
              return (
                <div key={j.id} className="border border-gray-200 rounded-xl bg-white">
                  <button
                    onClick={() => setExpandedHistoryId(expandedHistoryId === j.id ? null : j.id)}
                    className="w-full flex items-center justify-between p-3.5 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant={statusVariant}>{j.status}</Badge>
                      <span className="text-sm text-gray-900 font-medium">Job #{j.id}</span>
                      <span className="text-xs text-gray-500">{fmtRelative(j.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">
                        {j.queries_run ?? 0}/{j.search_count ?? 0} queries
                      </span>
                      {j.new_prospects_found != null && (
                        <span className="text-sm text-gray-600">{j.new_prospects_found} new</span>
                      )}
                      <ChevronDown size={16} className={`text-gray-400 transition-transform ${expandedHistoryId === j.id ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {expandedHistoryId === j.id && (
                    <JobHistoryDetail jobId={j.id} job={j} />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/** Expanded detail panel for a single history job — lazily fetches full search data. */
function JobHistoryDetail({ jobId, job }: { jobId: number; job: any }) {
  const { data } = useQuery({
    queryKey: ['ig-job-progress', jobId],
    queryFn: () => igDiscoveryApi.getJobProgress(jobId),
  })
  const detail = data?.data ?? null
  const searches: any[] = detail?.searches ?? detail?.query_results ?? job.query_results ?? []

  return (
    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <p className="text-xs text-gray-500">Started</p>
          <p className="text-gray-800">{fmtDate(job.started_at || job.created_at)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Completed</p>
          <p className="text-gray-800">{job.completed_at ? fmtDate(job.completed_at) : '--'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Queries Run</p>
          <p className="text-gray-800">{job.queries_run ?? 0} / {job.search_count ?? 0}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">New Prospects</p>
          <p className="text-gray-800 font-semibold">{job.new_prospects_found ?? 0}</p>
        </div>
      </div>
      {searches.length > 0 && (
        <div className="mt-3">
          <p className="text-xs text-gray-500 mb-1">Results by query:</p>
          <div className="space-y-1">
            {searches.map((qr: any, i: number) => {
              const status = qr.status || 'completed'
              const statusIcon = status === 'completed'
                ? <CheckCircle2 size={12} className="text-green-500 flex-shrink-0" />
                : status === 'failed'
                  ? <XCircle size={12} className="text-red-500 flex-shrink-0" />
                  : <Clock size={12} className="text-gray-400 flex-shrink-0" />
              return (
                <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                  <div className="flex items-center gap-1.5">
                    {statusIcon}
                    <span className="text-gray-700">{qr.label || qr.query_text || `Query ${i + 1}`}</span>
                  </div>
                  <span className="text-gray-500">{qr.count ?? qr.results_count ?? 0} found</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
      {job.error_message && (
        <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
          <p className="font-medium">Issues:</p>
          <p>{job.error_message}</p>
        </div>
      )}
    </div>
  )
}

// =========================
// Tab 4: Search Queries
// =========================

function QueriesTab({ confirm }: { confirm: ReturnType<typeof useConfirm>[1] }) {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingQuery, setEditingQuery] = useState<any>(null)
  const [form, setForm] = useState({ query_text: '', label: '', category: 'general', priority: '5' })

  const { data, isLoading } = useQuery({
    queryKey: ['ig-queries'],
    queryFn: () => igDiscoveryApi.listQueries(),
  })

  const seedMut = useMutation({
    mutationFn: () => igDiscoveryApi.seedQueries(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-queries'] }),
  })

  const createMut = useMutation({
    mutationFn: (data: { query_text: string; label: string; category?: string; priority?: number }) =>
      igDiscoveryApi.createQuery(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-queries'] }); closeModal() },
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      igDiscoveryApi.updateQuery(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ig-queries'] }); closeModal() },
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => igDiscoveryApi.deleteQuery(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-queries'] }),
  })

  const toggleMut = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      igDiscoveryApi.updateQuery(id, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-queries'] }),
  })

  const queries: any[] = data?.data ?? []

  const openAdd = () => {
    setEditingQuery(null)
    setForm({ query_text: '', label: '', category: 'general', priority: '5' })
    setModalOpen(true)
  }

  const openEdit = (q: any) => {
    setEditingQuery(q)
    setForm({ query_text: q.query_text, label: q.label, category: q.category || 'general', priority: String(q.priority ?? 5) })
    setModalOpen(true)
  }

  const closeModal = () => { setModalOpen(false); setEditingQuery(null) }

  const handleSave = () => {
    const payload = { query_text: form.query_text, label: form.label, category: form.category, priority: Number(form.priority) }
    if (editingQuery) {
      updateMut.mutate({ id: editingQuery.id, data: payload })
    } else {
      createMut.mutate(payload)
    }
  }

  const handleDelete = async (q: any) => {
    const ok = await confirm({ message: `Delete query "${q.label}"?`, title: 'Delete Query', confirmLabel: 'Delete' })
    if (ok) deleteMut.mutate(q.id)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">Configure what Instagram searches to run for finding sellers.</p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => seedMut.mutate()} loading={seedMut.isPending}>
            <Sparkles size={16} /> Seed Defaults
          </Button>
          <Button onClick={openAdd}><Plus size={16} /> Add Query</Button>
        </div>
      </div>

      {isLoading ? (
        <LoadingState message="Loading queries..." />
      ) : queries.length === 0 ? (
        <EmptyState icon={Search} title="No search queries configured"
          description="Add queries manually or click 'Seed Defaults' to load 14 pre-configured seller searches."
          action={<Button onClick={() => seedMut.mutate()} loading={seedMut.isPending}><Sparkles size={16} /> Seed Defaults</Button>} />
      ) : (
        <div className="space-y-2">
          {queries.map((q: any) => (
            <div key={q.id} className="border border-gray-200 rounded-xl bg-white p-4 flex items-center gap-4 flex-wrap">
              <button onClick={() => toggleMut.mutate({ id: q.id, is_active: !q.is_active })} className="flex-shrink-0"
                title={q.is_active ? 'Active - click to disable' : 'Inactive - click to enable'}>
                {q.is_active ? <ToggleRight size={24} className="text-green-500" /> : <ToggleLeft size={24} className="text-gray-300" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-900">{q.label}</span>
                  {q.category && <Badge>{q.category}</Badge>}
                  <span className="text-xs text-gray-400">Priority: {q.priority ?? 5}</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{q.query_text}</p>
                <div className="flex gap-4 text-xs text-gray-400 mt-1">
                  {q.last_run_at && <span>Last run: {fmtRelative(q.last_run_at)}</span>}
                  {q.results_count != null && <span>{q.results_count} results</span>}
                </div>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => openEdit(q)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded hover:bg-gray-100">
                  <Eye size={16} />
                </button>
                <button onClick={() => handleDelete(q)} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-gray-100">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Query Modal */}
      <Modal open={modalOpen} onClose={closeModal} title={editingQuery ? 'Edit Query' : 'Add Search Query'}>
        <div className="space-y-4">
          <Input label="Label" placeholder="e.g. Fashion sellers India" value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
          <Textarea label="Search Query" placeholder="e.g. #fashionstore #shopnow OR #boutiqueindia" value={form.query_text}
            onChange={(e) => setForm((f) => ({ ...f, query_text: e.target.value }))} />
          <Select label="Category" options={QUERY_CATEGORIES.filter((c) => c.value !== '')} value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} />
          <Input label="Priority (1-10, higher runs first)" type="number" min="1" max="10" value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleSave} loading={createMut.isPending || updateMut.isPending}
              disabled={!form.query_text.trim() || !form.label.trim()}>
              {editingQuery ? 'Save Changes' : 'Create Query'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// =========================
// Tab 5: Stats (Analytics Dashboard)
// =========================

const FUNNEL_STAGES = [
  { key: 'discovered', label: 'Discovered', color: '#6366f1' },
  { key: 'researching', label: 'Researching', color: '#818cf8' },
  { key: 'qualified', label: 'Qualified', color: '#a78bfa' },
  { key: 'outreach_sent', label: 'Outreach Sent', color: '#f59e0b' },
  { key: 'responded', label: 'Responded', color: '#34d399' },
  { key: 'converted', label: 'Converted', color: '#10b981' },
]

const SCORE_BUCKETS = [
  { label: '0 - 25', min: 0, max: 25, color: 'bg-blue-500' },
  { label: '26 - 50', min: 26, max: 50, color: 'bg-amber-500' },
  { label: '51 - 75', min: 51, max: 75, color: 'bg-orange-500' },
  { label: '76 - 100', min: 76, max: 100, color: 'bg-red-500' },
]

function fmtAxisTick(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`
  return String(value)
}

function StatsTab() {
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager'
  const queryClient = useQueryClient()
  const [subTab, setSubTab] = useState(() => {
    const saved = localStorage.getItem('ig_stats_subtab')
    return saved && ['overview', 'trends', 'hashtags', 'team'].includes(saved) ? saved : 'overview'
  })

  const subTabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'trends', label: 'Trends' },
    { key: 'hashtags', label: 'Hashtags' },
    ...(isAdmin ? [{ key: 'team', label: 'Team' }] : []),
  ]

  return (
    <div className="space-y-4">
      <ScrollableTabs tabs={subTabs} activeKey={subTab} onChange={(key) => { setSubTab(key); localStorage.setItem('ig_stats_subtab', key) }} variant="pill" />
      {subTab === 'overview' && <StatsOverview />}
      {subTab === 'trends' && <StatsTrends />}
      {subTab === 'hashtags' && <StatsHashtags queryClient={queryClient} />}
      {subTab === 'team' && isAdmin && <StatsTeam />}
    </div>
  )
}

/* ---- Overview Sub-tab ---- */

function StatsOverview() {
  const { data, isLoading } = useQuery({
    queryKey: ['ig-stats'],
    queryFn: () => igDiscoveryApi.getStats(),
  })

  const { data: queriesData } = useQuery({
    queryKey: ['ig-queries'],
    queryFn: () => igDiscoveryApi.listQueries(),
  })

  if (isLoading) return <LoadingState message="Loading analytics..." />

  const stats: any = data?.data ?? {}
  const byStage: Record<string, number> = stats.by_stage || {}
  const totalProspects: number = stats.total_prospects ?? 0
  const conversionRate: number = stats.conversion_rate ?? 0
  const pipelineAtRisk: number = stats.pipeline_at_risk ?? 0
  const discoveryEfficiency = stats.discovery_efficiency ?? '--'
  const totalOutreach: number = stats.total_outreach ?? 0
  const scoreDist: Record<string, number> = stats.score_distribution || {}
  const followerTiers: any[] = stats.follower_tiers || []
  // stats.outreach_by_type and stats.outreach_by_channel available for future charts

  // Queries for top queries table
  const queries: any[] = (queriesData?.data ?? [])
    .filter((q: any) => q.results_count > 0)
    .sort((a: any, b: any) => (b.results_count ?? 0) - (a.results_count ?? 0))
    .slice(0, 10)

  // Build funnel data
  const funnelData = FUNNEL_STAGES.map((s, i) => {
    const count = byStage[s.key] ?? 0
    const prevCount = i > 0 ? (byStage[FUNNEL_STAGES[i - 1].key] ?? 0) : totalProspects
    const dropOff = prevCount > 0 ? ((1 - count / prevCount) * 100).toFixed(0) : '--'
    return { ...s, count, dropOff }
  })
  const maxFunnelCount = Math.max(...funnelData.map((d) => d.count), 1)

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label="Conversion Rate"
          value={`${conversionRate.toFixed(1)}%`}
          icon={TrendingUp}
          iconColor={conversionRate >= 5 ? 'text-green-600' : 'text-red-600'}
          iconBg={conversionRate >= 5 ? 'bg-green-50' : 'bg-red-50'}
          subtitle={`${stats.converted_count ?? byStage.converted ?? 0} of ${fmtNum(totalProspects)} converted`}
        />
        <KpiCard
          label="Pipeline at Risk"
          value={fmtNum(pipelineAtRisk)}
          icon={AlertTriangle}
          iconColor={pipelineAtRisk > 0 ? 'text-amber-600' : 'text-green-600'}
          iconBg={pipelineAtRisk > 0 ? 'bg-amber-50' : 'bg-green-50'}
          subtitle="Outreach without response"
        />
        <KpiCard
          label="Discovery Efficiency"
          value={discoveryEfficiency}
          icon={Zap}
          iconColor="text-indigo-600"
          iconBg="bg-indigo-50"
          subtitle="Prospects per job run"
        />
        <KpiCard
          label="Total Outreach"
          value={fmtNum(totalOutreach)}
          icon={Send}
          iconColor="text-indigo-600"
          iconBg="bg-indigo-50"
          subtitle="Total touchpoints"
        />
      </div>

      {/* Conversion Funnel */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Conversion Funnel</h3>
        {totalProspects === 0 ? (
          <p className="text-sm text-gray-400">No data yet. Run a discovery job to get started.</p>
        ) : (
          <div className="space-y-2.5">
            {funnelData.map((stage, i) => {
              const widthPct = Math.max((stage.count / maxFunnelCount) * 100, 2)
              return (
                <div key={stage.key} className="flex items-center gap-3">
                  <div className="w-28 flex-shrink-0 text-xs text-gray-600 font-medium">{stage.label}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden relative">
                    <div
                      className="h-full rounded-full transition-all flex items-center px-2"
                      style={{ width: `${widthPct}%`, backgroundColor: stage.color }}
                    >
                      <span className="text-white text-xs font-medium whitespace-nowrap">{stage.count}</span>
                    </div>
                  </div>
                  <div className="w-16 text-right text-xs text-gray-400 flex-shrink-0">
                    {i > 0 && stage.dropOff !== '--' ? `${stage.dropOff}% drop` : ''}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Score Distribution + Follower Tier */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Score Distribution */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Score Distribution</h3>
          {Object.keys(scoreDist).length === 0 ? (
            <p className="text-sm text-gray-400">No scores available yet. Enrich prospects to generate scores.</p>
          ) : (
            <div className="space-y-2">
              {SCORE_BUCKETS.map((bucket) => {
                const count = scoreDist[bucket.label] ?? 0
                const maxBucket = Math.max(...Object.values(scoreDist), 1)
                return (
                  <div key={bucket.label} className="flex items-center gap-3">
                    <span className="w-16 text-xs text-gray-600 flex-shrink-0">{bucket.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${bucket.color}`}
                        style={{ width: `${maxBucket > 0 ? Math.max((count / maxBucket) * 100, 2) : 0}%` }}
                      />
                    </div>
                    <span className="w-10 text-xs text-gray-600 text-right">{count}</span>
                  </div>
                )
              })}
              {stats.avg_score != null && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                  <ScoreBadge score={Math.round(stats.avg_score)} />
                  <span className="text-xs text-gray-500">average across all prospects</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Follower Tier Breakdown */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Follower Tier Breakdown</h3>
          {followerTiers.length === 0 ? (
            <p className="text-sm text-gray-400">No enriched prospects yet. Enrich prospects to see tier breakdown.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 text-gray-500 font-medium">Tier</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Count</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Avg Score</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Avg Eng.</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Conv Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {followerTiers.map((t: any) => (
                    <tr key={t.tier} className="border-b border-gray-50">
                      <td className="py-2 text-gray-800">
                        <span className="font-medium">{t.tier}</span>
                        <span className="text-gray-400 ml-1">({t.range})</span>
                      </td>
                      <td className="py-2 text-gray-700 text-right">{t.count}</td>
                      <td className="py-2 text-gray-700 text-right">{t.avg_score != null ? t.avg_score.toFixed(0) : '--'}</td>
                      <td className="py-2 text-gray-700 text-right">{t.avg_engagement != null ? t.avg_engagement.toFixed(1) + '%' : '--'}</td>
                      <td className="py-2 text-gray-700 text-right">{t.conversion_rate != null ? t.conversion_rate.toFixed(1) + '%' : '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Top Queries */}
      {queries.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Top Queries by Results</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-medium">Label</th>
                  <th className="text-left py-2 text-gray-500 font-medium">Category</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Results Found</th>
                </tr>
              </thead>
              <tbody>
                {queries.map((q: any) => (
                  <tr key={q.id} className="border-b border-gray-50">
                    <td className="py-2 text-gray-800 font-medium">{q.label}</td>
                    <td className="py-2"><Badge>{q.category || 'general'}</Badge></td>
                    <td className="py-2 text-gray-700 text-right font-medium">{fmtNum(q.results_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

/* ---- Trends Sub-tab ---- */

function StatsTrends() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ig-analytics-trends'],
    queryFn: () => igDiscoveryApi.getTrends(12),
    retry: 1,
  })

  if (isLoading) return <LoadingState message="Loading trends..." />

  const trends: any = data?.data ?? {}
  const weeks: any[] = trends.weeks || []

  if (isError || weeks.length === 0) {
    return (
      <EmptyState
        icon={TrendingUp}
        title="No trend data available"
        description="Trend analytics will appear once you have a few weeks of discovery activity."
      />
    )
  }

  const chartData = weeks.map((w: any) => ({
    name: w.label || w.week,
    'New Prospects': w.new_prospects ?? 0,
    Conversions: w.conversions ?? 0,
    Outreach: w.outreach ?? 0,
  }))

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Weekly Trends</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis width={35} tick={{ fontSize: 11 }} tickFormatter={fmtAxisTick} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="New Prospects" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} strokeWidth={2} />
              <Area type="monotone" dataKey="Conversions" stroke="#10b981" fill="#10b981" fillOpacity={0.15} strokeWidth={2} />
              <Area type="monotone" dataKey="Outreach" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

/* ---- Hashtags Sub-tab ---- */

function StatsHashtags({ queryClient }: { queryClient: ReturnType<typeof useQueryClient> }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ig-analytics-hashtags'],
    queryFn: () => igDiscoveryApi.getHashtagAnalytics(),
    retry: 1,
  })

  // Fallback: use stats popular_hashtags if analytics endpoint not available
  const { data: statsData } = useQuery({
    queryKey: ['ig-stats'],
    queryFn: () => igDiscoveryApi.getStats(),
  })

  const addQueryMut = useMutation({
    mutationFn: (hashtag: string) =>
      igDiscoveryApi.createQuery({ query_text: `#${hashtag}`, label: `#${hashtag}`, category: 'general', priority: 5 }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-queries'] }),
  })

  if (isLoading) return <LoadingState message="Loading hashtag analytics..." />

  const analytics: any = data?.data ?? {}
  const hashtagList: any[] = analytics.hashtags || []
  const suggestions: any[] = analytics.suggestions || []

  // Fall back to popular_hashtags from stats if analytics endpoint returned empty
  const fallbackHashtags: any[] = statsData?.data?.popular_hashtags || []
  const displayHashtags = hashtagList.length > 0 ? hashtagList : fallbackHashtags

  if (isError && displayHashtags.length === 0) {
    return (
      <EmptyState
        icon={Hash}
        title="No hashtag data yet"
        description="Hashtag analytics appear after discovery jobs find prospects with hashtags."
      />
    )
  }

  // Top 20 for the bar chart
  const barData = displayHashtags.slice(0, 20).map((h: any) => ({
    name: `#${h.hashtag || h.name}`,
    count: h.count ?? h.discovery_count ?? 0,
  }))

  return (
    <div className="space-y-6">
      {/* Horizontal Bar Chart */}
      {barData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Top Hashtags by Frequency</h3>
          <div style={{ height: Math.max(barData.length * 28 + 40, 200) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} layout="vertical" margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtAxisTick} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Quality Table */}
      {displayHashtags.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Hashtag Quality</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-gray-500 font-medium">Hashtag</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Prospects</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Avg Score</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Avg Engagement</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Conversions</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Conv Rate</th>
                </tr>
              </thead>
              <tbody>
                {displayHashtags.slice(0, 30).map((h: any) => {
                  const tag = h.hashtag || h.name
                  const prospects = h.count ?? h.discovery_count ?? 0
                  const conversions = h.conversions ?? 0
                  const convRate = prospects > 0 ? ((conversions / prospects) * 100).toFixed(1) : '0.0'
                  const hasConversions = conversions > 0
                  return (
                    <tr key={tag} className={`border-b border-gray-50 ${hasConversions ? 'bg-green-50/50' : ''}`}>
                      <td className="py-2 text-gray-800 font-medium">#{tag}</td>
                      <td className="py-2 text-gray-700 text-right">{prospects}</td>
                      <td className="py-2 text-gray-700 text-right">{h.avg_score != null ? Math.round(h.avg_score) : '--'}</td>
                      <td className="py-2 text-gray-700 text-right">{h.avg_engagement != null ? `${(h.avg_engagement * 100).toFixed(1)}%` : '--'}</td>
                      <td className="py-2 text-gray-700 text-right">{conversions}</td>
                      <td className="py-2 text-gray-700 text-right">{convRate}%</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Suggested Queries */}
      {suggestions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Suggested Queries</h3>
          <p className="text-xs text-gray-500 mb-3">High-value hashtags not yet in your search queries. Add them to discover more sellers.</p>
          <div className="space-y-2">
            {suggestions.map((s: any) => (
              <div key={s.hashtag} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-800">#{s.hashtag}</span>
                  <span className="text-xs text-gray-500 ml-2">{s.reason}</span>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => addQueryMut.mutate(s.hashtag)}
                  loading={addQueryMut.isPending}
                  className="ml-2 flex-shrink-0"
                >
                  <Plus size={14} /> Add to Queries
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ---- Team Sub-tab (admin/manager only) ---- */

function StatsTeam() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ig-analytics-team'],
    queryFn: () => igDiscoveryApi.getTeamPerformance(),
    retry: 1,
  })

  if (isLoading) return <LoadingState message="Loading team performance..." />

  const teamData: any = data?.data ?? {}
  const members: any[] = teamData.members || []

  if (isError || members.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No team data available"
        description="Team performance analytics will appear once team members start working on prospects."
      />
    )
  }

  // Sort by conversions (highest first)
  const sorted = [...members].sort((a: any, b: any) => (b.conversions ?? 0) - (a.conversions ?? 0))

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Team Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 text-gray-500 font-medium">Member</th>
                <th className="text-right py-2 text-gray-500 font-medium">Outreach Sent</th>
                <th className="text-right py-2 text-gray-500 font-medium">Prospects Assigned</th>
                <th className="text-right py-2 text-gray-500 font-medium">Conversions</th>
                <th className="text-right py-2 text-gray-500 font-medium">Conv Rate</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m: any) => {
                const assigned = m.prospects_assigned ?? 0
                const conversions = m.conversions ?? 0
                const convRate = assigned > 0 ? ((conversions / assigned) * 100).toFixed(1) : '0.0'
                return (
                  <tr key={m.user_id || m.name} className="border-b border-gray-50">
                    <td className="py-2 text-gray-800 font-medium">{m.name || m.username || `User ${m.user_id}`}</td>
                    <td className="py-2 text-gray-700 text-right">{m.outreach_sent ?? 0}</td>
                    <td className="py-2 text-gray-700 text-right">{assigned}</td>
                    <td className="py-2 text-gray-700 text-right font-medium">{conversions}</td>
                    <td className="py-2 text-gray-700 text-right">{convRate}%</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
